import { Box, Fade } from '@mui/material';
import type { ReactElement } from 'react';
import { useEffect, useState } from 'react';

import { useReducedMotion } from '@/hooks/useReducedMotion';
import { APP_VERSION } from '@/utils/version';

const STORAGE_KEY = 'sherlock.boot.played';
const TOTAL_DURATION_MS = 1800;

const LINES: Array<{ text: string; tone?: 'ok' | 'info' | 'accent' }> = [
  { text: `▓ SHERLOCK v${APP_VERSION} — boot`, tone: 'accent' },
  { text: '› init theme tokens .............. ok', tone: 'ok' },
  { text: '› authenticate user .............. ok', tone: 'ok' },
  { text: '› connect /api/sentry ............ 200', tone: 'ok' },
  { text: '› connect /api/datadog ........... 200', tone: 'ok' },
  { text: '› fetch /api/status .............. ready', tone: 'info' },
  { text: '› ready_', tone: 'accent' },
];

export function BootSequence({ onDone }: { onDone: () => void }): ReactElement | null {
  const reducedMotion = useReducedMotion();
  const [visibleCount, setVisibleCount] = useState(0);
  const [hidden, setHidden] = useState(false);

  useEffect(() => {
    if (reducedMotion) {
      try {
        sessionStorage.setItem(STORAGE_KEY, '1');
      } catch {
        /* swallow — storage not available */
      }
      onDone();
      return;
    }

    const perLine = TOTAL_DURATION_MS / LINES.length;
    const timers: number[] = [];
    for (let i = 1; i <= LINES.length; i += 1) {
      timers.push(window.setTimeout(() => setVisibleCount(i), perLine * i));
    }
    timers.push(
      window.setTimeout(() => {
        setHidden(true);
        try {
          sessionStorage.setItem(STORAGE_KEY, '1');
        } catch {
          /* swallow */
        }
        window.setTimeout(onDone, 200); // let fade-out finish
      }, TOTAL_DURATION_MS + 250),
    );

    function skip(): void {
      timers.forEach((t) => window.clearTimeout(t));
      setHidden(true);
      try {
        sessionStorage.setItem(STORAGE_KEY, '1');
      } catch {
        /* swallow */
      }
      window.setTimeout(onDone, 150);
    }
    window.addEventListener('keydown', skip);
    window.addEventListener('click', skip);
    // #6: mobile thumb skip — passive so we don't block scroll
    window.addEventListener('touchstart', skip, { passive: true });

    return () => {
      timers.forEach((t) => window.clearTimeout(t));
      window.removeEventListener('keydown', skip);
      window.removeEventListener('click', skip);
      window.removeEventListener('touchstart', skip);
    };
  }, [reducedMotion, onDone]);

  if (reducedMotion) return null;

  return (
    <Fade in={!hidden} timeout={200} unmountOnExit>
      <Box
        sx={{
          position: 'fixed',
          inset: 0,
          backgroundColor: 'background.deeper',
          zIndex: 9999,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontFamily: '"JetBrains Mono", monospace',
          fontSize: { xs: 11, md: 13 },
          color: 'text.primary',
        }}
        aria-hidden="true"
      >
        <Box sx={{ width: 'min(92vw, 540px)' }}>
          {LINES.slice(0, visibleCount).map((line, i) => (
            <Box
              key={i}
              sx={{
                py: 0.4,
                color:
                  line.tone === 'accent'
                    ? 'primary.main'
                    : line.tone === 'ok'
                      ? 'success.main'
                      : line.tone === 'info'
                        ? 'info.main'
                        : 'text.primary',
                textShadow: line.tone === 'accent' ? '0 0 6px rgba(217, 119, 87, 0.55)' : 'none',
              }}
            >
              {line.text}
              {i === visibleCount - 1 && i === LINES.length - 1 && (
                <Box
                  component="span"
                  sx={{
                    display: 'inline-block',
                    width: 8,
                    height: 14,
                    backgroundColor: 'primary.main',
                    boxShadow: '0 0 4px rgba(217, 119, 87, 0.55)',
                    animation: 'blink 1.1s steps(1) infinite',
                    ml: 0.5,
                    verticalAlign: 'middle',
                  }}
                />
              )}
            </Box>
          ))}
        </Box>
      </Box>
    </Fade>
  );
}

export function shouldPlayBoot(): boolean {
  try {
    return sessionStorage.getItem(STORAGE_KEY) !== '1';
  } catch {
    return false;
  }
}
